import { useEffect, useState, useCallback } from "react";
import api from "@/lib/api";
import { useApp } from "@/context/AppContext";
import { PageHeader, Card, Badge, EmptyState } from "@/components/Primitives";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "@/components/ui/table";
import { Trophy, ArrowUpRight } from "lucide-react";

export default function Rankings() {
  const { selectedDept, deptName, settings } = useApp();
  const [rows, setRows] = useState([]);
  const threshold = settings?.low_performer_threshold || 60;

  const load = useCallback(() => {
    api.get("/rankings", { params: { department_id: selectedDept !== "all" ? selectedDept : undefined } })
      .then((r) => setRows(r.data)).catch(() => {});
  }, [selectedDept]);

  useEffect(() => { load(); }, [load]);

  const medal = (i) => (i === 0 ? "#C9A24B" : i === 1 ? "#94a3b8" : i === 2 ? "#b45309" : "#475569");

  return (
    <div>
      <PageHeader title="Team Rankings" subtitle="Leaderboard by readiness & assessment performance." />
      <Card className="p-0 overflow-hidden">
        {rows.length === 0 ? (
          <EmptyState icon={Trophy} title="No members to rank" hint="Add team members and mark readiness to build the leaderboard." />
        ) : (
          <Table>
            <TableHeader>
              <TableRow className="border-slate-800 hover:bg-transparent">
                <TableHead className="w-16">Rank</TableHead>
                <TableHead>Member</TableHead>
                <TableHead>Department</TableHead>
                <TableHead>Readiness</TableHead>
                <TableHead>Avg Assessment</TableHead>
                <TableHead>Sessions</TableHead>
                <TableHead>Last Session</TableHead>
                <TableHead>Status</TableHead>
              </TableRow>
            </TableHeader>
            <TableBody>
              {rows.map((r, i) => (
                <TableRow key={r.member.id} data-testid={`ranking-row-${r.member.id}`} className="border-slate-800 hover:bg-slate-800/40">
                  <TableCell>
                    <span className="font-mono text-lg font-semibold" style={{ color: medal(i) }}>#{i + 1}</span>
                  </TableCell>
                  <TableCell>
                    <p className="text-white text-sm">{r.member.name}</p>
                    <p className="text-slate-500 text-xs font-mono">{r.member.employee_id}</p>
                  </TableCell>
                  <TableCell className="text-slate-400 text-sm">{deptName(r.member.department_id)}</TableCell>
                  <TableCell>
                    <span className="font-mono text-sm" style={{ color: r.readiness_pct >= 90 ? "#059669" : r.readiness_pct < threshold ? "#DC2626" : "#C9A24B" }}>
                      {r.readiness_pct}%
                    </span>
                  </TableCell>
                  <TableCell className="font-mono text-slate-300 text-sm">{r.avg_assessment ?? "—"}</TableCell>
                  <TableCell className="font-mono text-slate-400 text-sm">{r.sessions_attended}</TableCell>
                  <TableCell className="font-mono text-slate-500 text-xs">{r.last_session_date || "—"}</TableCell>
                  <TableCell>
                    {r.low_performer
                      ? <Badge tone="red">Needs Support</Badge>
                      : r.readiness_pct >= 90
                        ? <Badge tone="green"><ArrowUpRight className="h-3 w-3 mr-1" />Top Performer</Badge>
                        : <Badge tone="gold">On Track</Badge>}
                  </TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        )}
      </Card>
    </div>
  );
}
